import { useState, useEffect, useMemo } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { usePins, usePinFilters } from "@/lib/pins-context";
import { Button } from "@/components/ui/button";
import { Briefcase, Building2, Calendar, CalendarHeart, Check, ChevronDown, ChevronLeft, ChevronRight, Clock, FileStack, FileText, Globe, GraduationCap, Heart, Home, LayoutDashboard, LayoutGrid, LogOut, Mail, Map, MapPin, MessageSquare, MessagesSquare, Package, Pin, PinOff, Radio, Receipt, Settings, ShoppingBag, Tags, Trophy, Users, Shield, UsersRound, Menu, Wrench, X } from "lucide-react";
import { useMediaQuery } from "@/lib/use-media-query";
import NotificationBell from "@/components/notification-bell";
import { SpectatorSponsorBanner } from "@/components/spectator-sponsor-banner";
import { UserMenu } from "@/components/user-menu";
import { ImpersonationBanner } from "@/components/impersonation-banner";
import { PersonaSwitcherSection, MobilePersonaSwitcherSection } from "@/components/persona-switcher-section";
import { useImpersonation } from "@/lib/impersonation-context";
import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import type { RoleType } from "@dugads/shared";

// Shell layout with sidebar -- per spec: Dugads Portal navigation
// Responsive: sidebar hidden below md, shown as drawer overlay on mobile

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  roles?: RoleType[];
  matchPaths?: RegExp;
}

interface NavGroup {
  label: string;
  icon: LucideIcon;
  roles?: RoleType[];
  items: NavItem[];
}

type NavEntry = NavItem | NavGroup;

// 186: a single primary destination in the mobile bottom tab bar. Tabs are a
// curated, persona-aware subset of the full nav tree (which stays reachable via
// the drawer behind "More"). `to` is the route a tap navigates to; `isActive`
// decides the highlighted tab (the "More" tab is special-cased to the drawer).
interface MobileTab {
  key: string;
  label: string;
  icon: LucideIcon;
  to?: string;
  isActive?: (pathname: string) => boolean;
}

function isNavGroup(entry: NavEntry): entry is NavGroup {
  return "items" in entry;
}

// 186: the label shown in the mobile top bar -- the active top-level nav entry's
// label (its matchPaths already cover detail routes, e.g. /events/:id -> Events).
// "/" (Dashboard) intentionally returns "" so the top bar shows the wordmark.
function activeNavLabel(entries: NavEntry[], pathname: string): string {
  if (pathname === "/") return "";
  for (const entry of entries) {
    if (isNavGroup(entry)) {
      const hit = entry.items.find((i) => isNavItemActive(i, pathname));
      if (hit) return hit.label;
    } else if (isNavItemActive(entry, pathname)) {
      return entry.label;
    }
  }
  return "";
}

const CALENDAR_PATH_MATCH = /\/calendar(?:\/|$)/;

// Active-route resolver for sidebar nav. React Router's NavLink default does a
// startsWith prefix match -- that breaks the moment two top-level nav items share
// a path prefix (e.g. clicking "Events" lands on /schools/<id>/calendar after the
// single-school redirect, which then prefix-matches the /schools NavLink and lights
// up "Schools" instead of "Events"). Items can override default startsWith via
// `matchPaths` to declare exactly which paths they own.
function isNavItemActive(item: NavItem, pathname: string): boolean {
  if (item.matchPaths) return item.matchPaths.test(pathname);
  if (item.to === "/") return pathname === "/";
  return pathname === item.to || pathname.startsWith(item.to + "/");
}

// School-scoped nested routes (calendar, events, teams, players, courts, zones,
// parent-invites, practice-series) live under /schools/<id>/<resource> but are
// conceptually part of the top-level resource, not Schools.
const SCHOOLS_LIST_MATCH = /^\/schools(?:\/new|\/[^/]+(?:\/edit)?)?$/;
const MY_EVENTS_MATCH = /^\/my-events(?:\/.*)?$/;
const EVENTS_MATCH =
  /^\/(events(?:\/.*)?|schools\/[^/]+\/(?:events(?:\/.*)?|calendar|practice-series))$/;
const TEAMS_MATCH = /^\/(teams(?:\/.*)?|schools\/[^/]+\/teams(?:\/.*)?)$/;
const PLAYERS_MATCH = /^\/(players(?:\/.*)?|schools\/[^/]+\/players(?:\/.*)?)$/;
const COACHES_MATCH = /^\/coaches(?:\/.*)?$/;
const COURTS_MATCH = /^\/(courts(?:\/.*)?|schools\/[^/]+\/courts(?:\/.*)?)$/;
const ZONES_MATCH = /^\/(zones(?:\/.*)?|schools\/[^/]+\/zones(?:\/.*)?)$/;
const PARENT_INVITES_MATCH =
  /^\/(parent-invites(?:\/.*)?|schools\/[^/]+\/parent-invites(?:\/.*)?)$/;

const allNavEntries: NavEntry[] = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  // 130 -- spectator timetable (My Kids / My Schools / My Favourites / All).
  // Parents land here; managers also get the cross-school admin list below.
  { to: "/my-events", label: "My Events", icon: CalendarHeart, roles: ["super_parent", "parent"], matchPaths: MY_EVENTS_MATCH },
  { to: "/events", label: "Events", icon: Calendar, roles: ["super_admin", "org_admin", "school_admin", "coach", "super_parent"], matchPaths: EVENTS_MATCH },
  { to: "/teams", label: "Teams", icon: Shield, roles: ["super_admin", "org_admin", "school_admin", "coach", "super_parent"], matchPaths: TEAMS_MATCH },
  { to: "/players", label: "Players", icon: Users, roles: ["super_admin", "school_admin", "coach", "super_parent"], matchPaths: PLAYERS_MATCH },
  { to: "/coaches", label: "Coaches", icon: UsersRound, roles: ["super_admin", "org_admin", "school_admin", "coach"], matchPaths: COACHES_MATCH },
  { to: "/discover", label: "Discover Events", icon: Globe },
  { to: "/live-coaches", label: "Live Coaches", icon: Radio, roles: ["super_admin", "org_admin", "school_admin", "coach", "super_parent"] },
  { to: "/my-children", label: "My Children", icon: Heart, roles: ["parent"] },
  // 119 -- minimal player home; visible only to the `player` role.
  { to: "/me/player-home", label: "My Home", icon: Home, roles: ["player"] },
  {
    label: "Organization", icon: Building2, roles: ["super_admin", "org_admin", "school_admin"],
    items: [
      { to: "/users", label: "Users", icon: UsersRound, roles: ["super_admin", "org_admin", "school_admin"] },
      { to: "/organizations", label: "Organizations", icon: Building2, roles: ["super_admin", "org_admin"] },
      { to: "/schools", label: "Schools & Venues", icon: GraduationCap, roles: ["super_admin", "org_admin"], matchPaths: SCHOOLS_LIST_MATCH },
      { to: "/parent-invites", label: "Parent Invites", icon: UsersRound, roles: ["super_admin", "school_admin"], matchPaths: PARENT_INVITES_MATCH },
      { to: "/zones", label: "Zones", icon: Map, roles: ["super_admin", "org_admin", "school_admin"], matchPaths: ZONES_MATCH },
    ],
  },
  {
    label: "Facilities", icon: MapPin, roles: ["super_admin", "org_admin", "school_admin", "coach", "super_parent"],
    items: [
      { to: "/courts", label: "Courts & Fields", icon: MapPin, roles: ["super_admin", "org_admin", "school_admin", "coach", "super_parent"], matchPaths: COURTS_MATCH },
      { to: "/court-templates", label: "Court Templates", icon: FileStack, roles: ["super_admin", "org_admin"] },
      { to: "/admin/venues", label: "Venues", icon: Map, roles: ["super_admin"] },
      { to: "/admin/pending-venues", label: "Pending Venues", icon: MapPin, roles: ["super_admin", "org_admin"] },
      { to: "/admin/age-groups", label: "Age Groups", icon: Clock, roles: ["super_admin"] },
    ],
  },
  {
    label: "Advertising", icon: Briefcase, roles: ["super_admin", "org_admin"],
    items: [
      { to: "/asset-types", label: "Asset Types", icon: Tags, roles: ["super_admin", "org_admin"] },
      { to: "/packages", label: "Packages", icon: Package, roles: ["super_admin", "org_admin"] },
      { to: "/sponsors", label: "Sponsors", icon: Briefcase, roles: ["super_admin", "org_admin"] },
      { to: "/placements", label: "Placements", icon: Receipt, roles: ["super_admin", "org_admin"] },
    ],
  },
  {
    label: "Shop", icon: ShoppingBag,
    items: [
      { to: "/shop", label: "Browse Shop", icon: ShoppingBag },
      { to: "/me/orders", label: "My Orders", icon: Receipt },
      { to: "/me/cashback", label: "My Cashback", icon: Receipt },
      { to: "/admin/shop/providers", label: "Shop Providers", icon: ShoppingBag, roles: ["super_admin"] },
      { to: "/admin/shop/orders", label: "Shop Orders", icon: Receipt, roles: ["super_admin"] },
    ],
  },
  { to: "/me/volunteer-offers", label: "My Volunteer Offers", icon: Heart },
  { to: "/messages", label: "Messages", icon: MessageSquare },
  // 140 -- two-way parent<->coach threads (distinct from broadcast Messages).
  { to: "/inbox", label: "Inbox", icon: MessagesSquare, roles: ["super_admin", "org_admin", "school_admin", "coach", "super_parent", "parent"] },
  // 114 -- inter-school fixture invitations (responder side: invited school staff).
  { to: "/fixture-invites", label: "Fixture Invites", icon: Calendar, roles: ["super_admin", "org_admin", "school_admin", "coach"] },
  { to: "/preferences", label: "Preferences", icon: Heart },
  {
    label: "Admin", icon: Wrench, roles: ["super_admin"],
    items: [
      { to: "/admin/emails", label: "Email Log", icon: Mail, roles: ["super_admin"] },
      { to: "/admin/policies", label: "Policies", icon: FileText, roles: ["super_admin"] },
      { to: "/admin/sports", label: "Sports Catalogue", icon: Trophy, roles: ["super_admin"] },
      { to: "/settings", label: "Settings", icon: Settings, roles: ["super_admin"] },
    ],
  },
];

const sponsorNavItems: NavItem[] = [
  { to: "/my-sponsor/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/placements", label: "Placements", icon: Receipt },
];


// Icon map for pin entity types
const pinIconMap: Record<string, LucideIcon> = {
  Organization: Building2,
  School: GraduationCap,
  Sponsor: Briefcase,
  Team: Shield,
  Event: Calendar,
};

const pinRouteMap: Record<string, string> = {
  Organization: "/organizations",
  School: "/schools",
  Sponsor: "/sponsors",
  Team: "/teams",
  Event: "/events",
};

function PinnedSection({ collapsed: isCollapsed }: { collapsed: boolean }) {
  const { pins, removePin } = usePins();
  const { activePinIds, togglePinFilter } = usePinFilters();

  return (
    <div className="border-b border-border p-2">
      {!isCollapsed && (
        <p className="mb-1 px-2 text-xs font-medium text-muted-foreground">Pinned</p>
      )}
      {pins.length === 0 && !isCollapsed && (
        <p className="px-2 py-1 text-xs text-muted-foreground/60">
          Use the Pin button on detail pages to bookmark items here.
        </p>
      )}
      {pins.length === 0 && isCollapsed && (
        <div className="flex justify-center py-1" title="No pinned items yet">
          <Pin className="h-3.5 w-3.5 text-muted-foreground/40" />
        </div>
      )}
      <div className="flex flex-col gap-0.5">
        {pins.map((pin) => {
          const Icon = pinIconMap[pin.entity_type] ?? MapPin;
          const route = `${pinRouteMap[pin.entity_type] ?? ""}/${pin.entity_id}`;
          const isActive = activePinIds.has(pin.id);
          return (
            <div key={pin.id} className="group flex min-w-0 items-center">
              {!isCollapsed && (
                <button
                  type="button"
                  onClick={() => togglePinFilter(pin.id)}
                  className={`ml-1 mr-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border transition-colors ${
                    isActive
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-muted-foreground/40 hover:border-muted-foreground"
                  }`}
                  title={isActive ? "Remove from filter" : "Add to filter"}
                >
                  {isActive && <Check className="h-3 w-3" />}
                </button>
              )}
              <NavLink
                to={route}
                title={isCollapsed ? pin.entity_name : undefined}
                className={({ isActive: routeActive }) =>
                  `flex min-w-0 flex-1 items-center rounded-md text-sm transition-colors ${
                    isCollapsed ? "justify-center px-2 py-1.5" : "gap-2 px-2 py-1.5"
                  } ${
                    routeActive
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`
                }
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                {!isCollapsed && (
                  <span className="flex-1 truncate text-xs" title={pin.entity_name}>{pin.entity_name}</span>
                )}
              </NavLink>
              {!isCollapsed && (
                <button
                  type="button"
                  onClick={() => removePin(pin.id)}
                  className="invisible mr-1 rounded p-0.5 text-muted-foreground hover:text-destructive group-hover:visible"
                  title="Unpin"
                >
                  <PinOff className="h-3 w-3" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MobilePinnedSection() {
  const { pins, removePin } = usePins();
  const { activePinIds, togglePinFilter } = usePinFilters();

  return (
    <div className="border-b border-border px-2 py-2">
      <p className="mb-1 px-2 text-xs font-medium text-muted-foreground">Pinned</p>
      {pins.length === 0 && (
        <p className="px-2 py-1 text-xs text-muted-foreground/60">
          Use the Pin button on detail pages to bookmark items here.
        </p>
      )}
      <div className="flex flex-col gap-0.5">
        {pins.map((pin) => {
          const Icon = pinIconMap[pin.entity_type] ?? MapPin;
          const route = `${pinRouteMap[pin.entity_type] ?? ""}/${pin.entity_id}`;
          const isActive = activePinIds.has(pin.id);
          return (
            <div key={pin.id} className="group flex min-w-0 items-center">
              <button
                type="button"
                onClick={() => togglePinFilter(pin.id)}
                className={`ml-1 mr-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border transition-colors ${
                  isActive
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-muted-foreground/40 hover:border-muted-foreground"
                }`}
                title={isActive ? "Remove from filter" : "Add to filter"}
              >
                {isActive && <Check className="h-3 w-3" />}
              </button>
              <NavLink
                to={route}
                className={({ isActive: routeActive }) =>
                  `flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors ${
                    routeActive
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`
                }
              >
                <Icon className="h-3.5 w-3.5" />
                <span className="truncate text-xs" title={pin.entity_name}>{pin.entity_name}</span>
              </NavLink>
              <button
                type="button"
                onClick={() => removePin(pin.id)}
                className="invisible mr-1 rounded p-0.5 text-muted-foreground hover:text-destructive group-hover:visible"
                title="Unpin"
              >
                <PinOff className="h-3 w-3" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// 186: the role-filtered nav tree, shared by the desktop sidebar, the mobile
// drawer, and the mobile More screen so all three stay in sync.
function useNavEntries(): NavEntry[] {
  const { user } = useAuth();
  return useMemo((): NavEntry[] => {
    if (!user) return allNavEntries;
    const userRoleTypes = new Set(user.roles.map((r) => r.role_type));
    const isSponsorAdmin = user.roles.some(
      (r) => r.role_type === "sponsor_admin" && r.scope_type === "sponsor",
    );
    const isSponsorUser = user.roles.some(
      (r) => r.role_type === "sponsor_user" && r.scope_type === "sponsor",
    );
    const isAdmin = user.roles.some(
      (r) => r.role_type === "super_admin" || r.role_type === "org_admin" || r.role_type === "school_admin" || r.role_type === "coach" || r.role_type === "super_parent",
    );

    if (!isAdmin) {
      if (isSponsorAdmin) {
        return [
          ...sponsorNavItems,
          { to: "/my-sponsor/users", label: "Manage Users", icon: Users },
        ];
      }
      if (isSponsorUser) return sponsorNavItems;
    }

    function roleMatch(roles?: RoleType[]) {
      return !roles || roles.some((r) => userRoleTypes.has(r));
    }

    const filtered: NavEntry[] = [];
    for (const entry of allNavEntries) {
      if (isNavGroup(entry)) {
        if (!roleMatch(entry.roles)) continue;
        const groupItems = entry.items.filter((item) => roleMatch(item.roles));
        if (groupItems.length > 0) {
          filtered.push({ ...entry, items: groupItems });
        }
      } else {
        if (roleMatch(entry.roles)) filtered.push(entry);
      }
    }

    // For school_admin/coach: inject "My School" link after Dashboard
    const schoolRole = user.roles.find(
      (r) => (r.role_type === "school_admin" || r.role_type === "coach" || r.role_type === "super_parent") && r.scope_type === "school",
    );
    if (schoolRole && !userRoleTypes.has("super_admin") && !userRoleTypes.has("org_admin")) {
      const dashIdx = filtered.findIndex((e) => !isNavGroup(e) && e.to === "/");
      filtered.splice(dashIdx + 1, 0, {
        to: `/schools/${schoolRole.scope_id}`,
        label: "My School",
        icon: GraduationCap,
        // Match the school's own detail page only -- nested resources like
        // /schools/<id>/events or /schools/<id>/calendar belong to Events.
        matchPaths: new RegExp(`^/schools/${schoolRole.scope_id}(?:/edit)?$`),
      });
    }

    return filtered;
  }, [user]);
}

const ROLE_LABELS: Partial<Record<RoleType, string>> = {
  super_admin: "Administrator",
  org_admin: "Organization Admin",
  school_admin: "School Admin",
  coach: "Coach",
  super_parent: "Parent",
  parent: "Parent",
  player: "Player",
  sponsor_admin: "Sponsor",
  sponsor_user: "Sponsor",
};

function primaryRoleLabel(roles: { role_type: RoleType }[]): string {
  const order: RoleType[] = ["super_admin", "org_admin", "school_admin", "coach", "super_parent", "sponsor_admin", "sponsor_user", "parent", "player"];
  for (const rt of order) {
    if (roles.some((r) => r.role_type === rt)) return ROLE_LABELS[rt] ?? rt;
  }
  return "Member";
}

// 186: the mobile "More" screen (screenshot 10) -- the full-page menu behind the
// More tab. Profile card + an Organization-portal shortcut (admin groups) + the
// rest of the role-filtered nav as iOS-style grouped lists + sign out. Routed at
// /more so it participates in the stack-nav history; rendered inside Layout.
// The admin-heavy groups are consolidated into the portal card; everything else
// stays individually reachable here (the desktop sidebar still lists all of it).
const MORE_PORTAL_GROUPS = new Set(["Organization", "Advertising", "Facilities", "Admin"]);
const MORE_TAB_ROUTES = new Set(["/", "/events", "/players", "/my-events", "/my-children"]);

function MoreRow({ to, icon: Icon, label }: { to: string; icon: LucideIcon; label: string }) {
  return (
    <Link to={to} className="flex items-center gap-3 px-4 py-3 transition-colors active:bg-muted/50">
      <Icon className="h-[18px] w-[18px] shrink-0 text-muted-foreground" />
      <span className="flex-1 truncate text-sm font-medium">{label}</span>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50" />
    </Link>
  );
}

export function MoreScreen() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const navEntries = useNavEntries();
  const { isImpersonating, exitImpersonation } = useImpersonation();

  const u = user?.user;
  const name = [u?.first_name, u?.last_name].filter(Boolean).join(" ") || u?.email || "Account";
  const initial = (name.charAt(0) || "?").toUpperCase();
  const roleSub = user ? primaryRoleLabel(user.roles) : "";

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  const portalGroups = navEntries.filter((e): e is NavGroup => isNavGroup(e) && MORE_PORTAL_GROUPS.has(e.label));
  const orgPortalTo =
    ["/organizations", "/schools"].find((p) => portalGroups.some((g) => g.items.some((i) => i.to === p))) ??
    portalGroups[0]?.items[0]?.to;
  const sectionGroups = navEntries.filter((e): e is NavGroup => isNavGroup(e) && !MORE_PORTAL_GROUPS.has(e.label));
  const standaloneItems = navEntries.filter((e): e is NavItem => !isNavGroup(e) && !MORE_TAB_ROUTES.has(e.to));

  return (
    <div className="mx-auto flex max-w-md flex-col gap-4" data-screen="more">
      <Link
        to="/profile"
        data-action="more-profile"
        className="flex items-center gap-3 rounded-2xl border border-border bg-background p-4 shadow-sm transition-colors active:bg-muted/50"
      >
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-base font-semibold text-primary-foreground">
          {initial}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold">{name}</p>
          {roleSub && <p className="truncate text-xs text-muted-foreground">{roleSub}</p>}
        </div>
        <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground/50" />
      </Link>

      {orgPortalTo && (
        <Link
          to={orgPortalTo}
          data-action="more-org-portal"
          className="flex items-center gap-3 rounded-2xl border border-border bg-background p-4 shadow-sm transition-colors active:bg-muted/50"
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Building2 className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate font-semibold">Organization portal</p>
            <p className="truncate text-xs text-muted-foreground">Schools, advertising &amp; field view</p>
          </div>
          <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground/50" />
        </Link>
      )}

      {standaloneItems.length > 0 && (
        <div className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-background shadow-sm">
          {standaloneItems.map((item) => (
            <MoreRow key={item.to} to={item.to} icon={item.icon} label={item.label} />
          ))}
        </div>
      )}

      {sectionGroups.map((group) => (
        <div key={group.label}>
          <p className="mb-1.5 px-4 text-xs font-medium uppercase tracking-wide text-muted-foreground">{group.label}</p>
          <div className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-background shadow-sm">
            {group.items.map((item) => (
              <MoreRow key={item.to} to={item.to} icon={item.icon} label={item.label} />
            ))}
          </div>
        </div>
      ))}

      {isImpersonating && (
        <button
          type="button"
          onClick={() => void exitImpersonation()}
          className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-700 transition-colors active:bg-amber-100"
        >
          Exit impersonation
        </button>
      )}

      <button
        type="button"
        data-action="more-sign-out"
        onClick={handleLogout}
        className="flex items-center justify-center gap-2 rounded-2xl border border-border bg-background px-4 py-3 text-sm font-semibold text-destructive shadow-sm transition-colors active:bg-muted/50"
      >
        <LogOut className="h-4 w-4" />
        Sign out
      </button>
    </div>
  );
}

export default function Layout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar-collapsed") === "true");
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem("sidebar-expanded-groups");
      return stored ? new Set(JSON.parse(stored)) : new Set<string>();
    } catch { return new Set<string>(); }
  });

  function toggleGroup(label: string) {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      localStorage.setItem("sidebar-expanded-groups", JSON.stringify([...next]));
      return next;
    });
  }
  const navEntries = useNavEntries();

  // 186: render the mobile bottom tab bar (and dock the ad carousel above it)
  // only below md; the desktop sidebar is untouched. matchMedia is accurate from
  // first paint in a real browser, so we mount exactly one banner instance
  // (top on md+, docked above the tabs below md) -- no double impression-tracking.
  const isMdUp = useMediaQuery("(min-width: 768px)");

  // Persona-aware primary tabs: sponsor users get a 3-tab nav (Dashboard /
  // Placements / Inbox); everyone else gets the standard 5 (Home / Calendar /
  // Events / Players / More). Destinations adapt to the routes the role can see.
  const mobileTabs = useMemo<MobileTab[]>(() => {
    if (!user) return [];
    const roleTypes = new Set(user.roles.map((r) => r.role_type));
    const isAdminish = (["super_admin", "org_admin", "school_admin", "coach", "super_parent"] as RoleType[]).some((r) => roleTypes.has(r));
    const isSponsor = !isAdminish && (roleTypes.has("sponsor_admin") || roleTypes.has("sponsor_user"));
    if (isSponsor) {
      return [
        { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, to: "/my-sponsor/dashboard", isActive: (p) => p.startsWith("/my-sponsor") },
        { key: "placements", label: "Placements", icon: Receipt, to: "/placements", isActive: (p) => p.startsWith("/placements") },
        { key: "inbox", label: "Inbox", icon: MessagesSquare, to: "/inbox", isActive: (p) => p.startsWith("/inbox") },
      ];
    }
    const has = (to: string) => navEntries.some((e) => !isNavGroup(e) && e.to === to);
    const schoolRole = user.roles.find(
      (r) => (r.role_type === "school_admin" || r.role_type === "coach" || r.role_type === "super_parent") && r.scope_type === "school",
    );
    const calendarTo = schoolRole
      ? `/schools/${schoolRole.scope_id}/calendar`
      : has("/events") ? "/events" : has("/my-events") ? "/my-events" : "/";
    // A single-school user's bare /events redirects to the calendar, which would
    // collide with the Calendar tab -- point Events straight at the school's
    // events list so the two tabs stay distinct.
    const eventsTo = schoolRole
      ? `/schools/${schoolRole.scope_id}/events`
      : has("/events") ? "/events" : has("/my-events") ? "/my-events" : "/discover";
    const playersTo = has("/players")
      ? "/players"
      : has("/my-children") ? "/my-children" : has("/me/player-home") ? "/me/player-home" : "/";
    return [
      { key: "home", label: "Home", icon: Home, to: "/", isActive: (p) => p === "/" },
      { key: "calendar", label: "Calendar", icon: Calendar, to: calendarTo, isActive: (p) => CALENDAR_PATH_MATCH.test(p) },
      { key: "events", label: "Events", icon: Trophy, to: eventsTo, isActive: (p) => (EVENTS_MATCH.test(p) || MY_EVENTS_MATCH.test(p)) && !CALENDAR_PATH_MATCH.test(p) },
      { key: "players", label: "Players", icon: Users, to: playersTo, isActive: (p) => PLAYERS_MATCH.test(p) || /^\/my-children/.test(p) },
      { key: "more", label: "More", icon: LayoutGrid, to: "/more", isActive: (p) => p === "/more" },
    ];
  }, [user, navEntries]);

  function toggleCollapsed() {
    setCollapsed((prev) => {
      localStorage.setItem("sidebar-collapsed", String(!prev));
      return !prev;
    });
  }

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Close drawer on escape key
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setDrawerOpen(false);
    }
    if (drawerOpen) {
      document.addEventListener("keydown", onKeyDown);
      return () => document.removeEventListener("keydown", onKeyDown);
    }
  }, [drawerOpen]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [drawerOpen]);

  const { isImpersonating, exitImpersonation, realUserSnapshot } = useImpersonation();

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  // Persistent sponsor banner gating mirrors the real adFreeRoles: shown to
  // parent / player (and anonymous spectator, handled by MinimalLayout), hidden
  // for super_parent and every operator/sponsor role. super_parent perk: no ads.
  const adFreeRoles = new Set(["super_parent", "school_admin", "org_admin", "super_admin", "coach", "sponsor_admin", "sponsor_user"]);
  const showSponsorBanner =
    !!user &&
    user.roles.some((r) => r.role_type === "parent" || r.role_type === "player") &&
    !user.roles.some((r) => adFreeRoles.has(r.role_type));

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Desktop sidebar -- hidden below md, collapsible */}
      <aside className={`hidden flex-col overflow-hidden border-r border-border bg-muted/50 transition-all duration-200 md:flex ${collapsed ? "w-14" : "w-60"}`}>
        <div className={`flex h-14 items-center border-b border-border ${collapsed ? "justify-center px-1" : "justify-between px-4"}`}>
          {!collapsed && <span className="text-lg font-semibold">Dugads</span>}
          <div className="flex items-center gap-1">
            <NotificationBell />
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title={collapsed ? "Expand sidebar" : "Collapse sidebar"} onClick={toggleCollapsed}>
              {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        <PinnedSection collapsed={collapsed} />
        <PersonaSwitcherSection collapsed={collapsed} />

        <nav className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto p-2">
          {navEntries.map((entry) => {
            if (isNavGroup(entry)) {
              const isExpanded = expandedGroups.has(entry.label);
              const groupActive = entry.items.some((i) => isNavItemActive(i, location.pathname));
              return (
                <div key={entry.label}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(entry.label)}
                    title={collapsed ? entry.label : undefined}
                    className={`flex w-full items-center rounded-md text-sm font-medium transition-colors ${
                      collapsed ? "justify-center px-2 py-2" : "gap-3 px-3 py-2"
                    } ${
                      groupActive && !isExpanded
                        ? "text-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`}
                  >
                    <entry.icon className="h-4 w-4 shrink-0" />
                    {!collapsed && (
                      <>
                        <span className="flex-1 text-left">{entry.label}</span>
                        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isExpanded ? "" : "-rotate-90"}`} />
                      </>
                    )}
                  </button>
                  {isExpanded && !collapsed && (
                    <div className="ml-3 flex flex-col gap-0.5 border-l border-border pl-2">
                      {entry.items.map((item) => {
                        const active = isNavItemActive(item, location.pathname);
                        return (
                          <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === "/"}
                            aria-current={active ? "page" : undefined}
                            className={`flex items-center gap-3 rounded-md px-3 py-1.5 text-sm transition-colors ${
                              active
                                ? "bg-primary/10 text-primary font-medium"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            }`}
                          >
                            <item.icon className="h-3.5 w-3.5 shrink-0" />
                            {item.label}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            const active = isNavItemActive(entry, location.pathname);
            return (
              <NavLink
                key={entry.to}
                to={entry.to}
                end={entry.to === "/"}
                title={collapsed ? entry.label : undefined}
                aria-current={active ? "page" : undefined}
                className={`flex items-center rounded-md text-sm font-medium transition-colors ${
                  collapsed ? "justify-center px-2 py-2" : "gap-3 px-3 py-2"
                } ${
                  active
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                <entry.icon className="h-4 w-4 shrink-0" />
                {!collapsed && entry.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-border p-2">
          <UserMenu
            user={user?.user ?? null}
            realUser={isImpersonating ? realUserSnapshot : null}
            collapsed={collapsed}
            onLogout={handleLogout}
            onExitImpersonation={isImpersonating ? () => void exitImpersonation() : undefined}
          />
        </div>
      </aside>

      {/* Mobile drawer overlay -- visible below md when open */}
      {drawerOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            onClick={() => setDrawerOpen(false)}
          />
          <aside className="fixed inset-y-0 left-0 z-50 flex w-60 flex-col border-r border-border bg-background shadow-lg md:hidden">
            <div className="flex h-14 items-center justify-between border-b border-border px-4">
              <span className="text-lg font-semibold">Dugads</span>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setDrawerOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <MobilePinnedSection />
            <MobilePersonaSwitcherSection />
            <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto p-2">
              {navEntries.map((entry) => {
                if (isNavGroup(entry)) {
                  const isExpanded = expandedGroups.has(entry.label);
                  return (
                    <div key={entry.label}>
                      <button
                        type="button"
                        onClick={() => toggleGroup(entry.label)}
                        className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      >
                        <entry.icon className="h-4 w-4" />
                        <span className="flex-1 text-left">{entry.label}</span>
                        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isExpanded ? "" : "-rotate-90"}`} />
                      </button>
                      {isExpanded && (
                        <div className="ml-3 flex flex-col gap-0.5 border-l border-border pl-2">
                          {entry.items.map((item) => {
                            const active = isNavItemActive(item, location.pathname);
                            return (
                              <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === "/"}
                                aria-current={active ? "page" : undefined}
                                className={`flex items-center gap-3 rounded-md px-3 py-1.5 text-sm transition-colors ${
                                  active
                                    ? "bg-primary/10 text-primary font-medium"
                                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                                }`}
                              >
                                <item.icon className="h-3.5 w-3.5" />
                                {item.label}
                              </NavLink>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }
                const active = isNavItemActive(entry, location.pathname);
                return (
                  <NavLink
                    key={entry.to}
                    to={entry.to}
                    end={entry.to === "/"}
                    aria-current={active ? "page" : undefined}
                    className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      active
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`}
                  >
                    <entry.icon className="h-4 w-4" />
                    {entry.label}
                  </NavLink>
                );
              })}
            </nav>
            <div className="border-t border-border p-2">
              <UserMenu
                user={user?.user ?? null}
                realUser={isImpersonating ? realUserSnapshot : null}
                onLogout={handleLogout}
                onExitImpersonation={isImpersonating ? () => void exitImpersonation() : undefined}
              />
            </div>
          </aside>
        </>
      )}

      {/* Main content */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Mobile top bar -- visible below md. Left slot: Back on a pushed
            detail, the Dugads wordmark on the Dashboard root, a hamburger when
            the persona has no "More" tab (sponsor), else a spacer to keep the
            centered title balanced. */}
        {(() => {
          const pathname = location.pathname;
          const isDashboardRoot = pathname === "/";
          const rootPaths = new Set(mobileTabs.map((t) => t.to).filter(Boolean) as string[]);
          rootPaths.add("/");
          const showBack = !rootPaths.has(pathname);
          const hasMoreTab = mobileTabs.some((t) => t.key === "more");
          const title =
            pathname === "/more" ? "More"
            : pathname === "/notifications" ? "Notifications"
            : CALENDAR_PATH_MATCH.test(pathname) ? "Calendar"
            : activeNavLabel(navEntries, pathname);
          return (
            <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-3 md:hidden">
              {showBack ? (
                <Button variant="ghost" size="sm" className="h-9 w-9 p-0" onClick={() => navigate(-1)} aria-label="Back" data-action="mobile-back">
                  <ChevronLeft className="h-5 w-5" />
                </Button>
              ) : isDashboardRoot ? (
                <span className="text-lg font-semibold">Dugads</span>
              ) : !hasMoreTab ? (
                <Button variant="ghost" size="sm" className="h-9 w-9 p-0" onClick={() => setDrawerOpen(true)} aria-label="Open menu">
                  <Menu className="h-5 w-5" />
                </Button>
              ) : (
                <span className="h-9 w-9 shrink-0" aria-hidden />
              )}
              <span className="flex-1 truncate text-center text-base font-semibold">
                {isDashboardRoot ? "" : title}
              </span>
              <NotificationBell />
            </header>
          );
        })()}

        <ImpersonationBanner />
        {/* One banner instance: top on md+, docked above the tab bar below md. */}
        {showSponsorBanner && isMdUp && <SpectatorSponsorBanner />}

        {/* 186: mobile uses a muted "grouped list" canvas so white cards lift
            off it (matches the prototype's #f3f3f3 backdrop); desktop stays on
            the default white background. */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-muted md:overflow-auto md:bg-transparent">
          <div className="p-4 md:p-6">{children}</div>
        </main>

        {/* Mobile dock: sponsor ad carousel above a persistent bottom tab bar. */}
        {!isMdUp && (
          <div className="shrink-0 md:hidden">
            {showSponsorBanner && <SpectatorSponsorBanner />}
            <nav
              className="flex items-stretch border-t border-border bg-background/95 pb-[env(safe-area-inset-bottom)] backdrop-blur"
              data-region="mobile-tab-bar"
            >
              {mobileTabs.map((tab) => {
                const active = tab.isActive?.(location.pathname) ?? false;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    data-tab={tab.key}
                    aria-current={active ? "page" : undefined}
                    onClick={() => {
                      setDrawerOpen(false);
                      if (tab.to) navigate(tab.to);
                    }}
                    className={`flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 py-1.5 text-[10px] font-medium transition-transform active:scale-[.96] ${
                      active ? "text-primary" : "text-muted-foreground/60"
                    }`}
                  >
                    <tab.icon className="h-[23px] w-[23px]" strokeWidth={active ? 2.25 : 2} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        )}
      </div>
    </div>
  );
}
